import posthog from "posthog-js";
import { createLogger } from "./logger";

const log = createLogger("PostHog");

/**
 * Environment classification for analytics, derived from the origin the
 * bundle is actually served from - NOT from a build-time flag. The deployed
 * prod bundle historically shipped with REACT_APP_POSTHOG_ENV=development,
 * which poisoned the `environment` property on every event and forced all
 * prod/dev splits to filter on $current_url instead. The URL host is the
 * ground truth the URL filter already relied on, so registering it as the
 * super property makes `environment` reliable for the first time.
 *
 * Hosts observed in live analytics data: the three deployed origins below,
 * localhost, and stray direct-to-ALB IP hits (classified "other").
 */
export const deriveEnvironment = (host: string): "prod" | "dev" | "demo" | "local" | "other" => {
  if (host === "prod.contract.space") return "prod";
  if (host === "dev.contract.space") return "dev";
  if (host === "demo.contract.space") return "demo";
  if (host.startsWith("localhost") || host.startsWith("127.")) return "local";

  return "other";
};

export const initPostHog = (): void => {
  const key = process.env.REACT_APP_POSTHOG_API_KEY;
  const host = process.env.REACT_APP_POSTHOG_HOST ?? "https://app.posthog.com";
  const enabled = process.env.REACT_APP_POSTHOG_ON === "true";
  const environment = deriveEnvironment(window.location.host);

  if (!key) {
    log.warn("POSTHOG API key not set. Skipping PostHog init.");

    return;
  }

  if (!enabled) {
    log.info("PostHog is disabled via environment variable. Skipping PostHog init.");

    return;
  }

  // Don't auto-capture everything by default if you want fine-grained control
  posthog.init(key, {
    api_host: host,
    autocapture: false, // set true if you want automatic DOM/event capture
    disable_session_recording: false, // Ensure this is false to allow recording
    enable_recording_console_log: true, // Recommended for better debugging
    capture_pageview: true, // we'll handle SPA pageviews manually

    loaded: (posthog) => {
      // Set custom session recording options if needed
      posthog.startSessionRecording();
    },
  });

  // Super properties sent with every event. `app_version` is the git sha (or
  // "unknown") inlined by webpack DefinePlugin, so analytics can be broken
  // down by deployed build. More are registered as they become known during
  // auth (auth_tier, tenant_id, office host/platform) - see analytics.ts.
  posthog.register({
    environment,
    app_version: process.env.APP_VERSION ?? "unknown",
  });

  // Only log initialization outside prod
  if (environment !== "prod") {
    log.info(`PostHog initialized for ${environment} environment`);
  }
};

export default posthog;
